import React, {useMemo, useState, useEffect, useCallback} from "react";
import {useUser} from "../../../util/user";
import Noti from "../../../util/noti";
import {styleDetails, optionsTable} from "./styleTable";
import {SITEOTHERSCOLS} from "./../../../util/columns";
import MaterialTable from "material-table";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import {useUserProps} from "./../../../util/userDBContext";
import Loader from "./../../general/Loader";
import { useRouteWithData } from "../../../util/router";

function AccountingTable (props) {
	const user = useUser();
	const [ready, setReady] = useState(false); // table is ready or not
	const {loggedCountry} = useUserProps();

	const { openLink } = useRouteWithData();

	const [data, setData] = useState([]); // data for table
	const [acts, setActs] = useState([]); // actions for table

	const [needLoader, setNeedLoader] = useState(false); // need loader or not
	const [showLoader, setShowLoader] = useState({position: "relative"});

	const otherscols = useMemo(() => SITEOTHERSCOLS, []); // columns for PM and ACCT

	const siteView = user.roles.includes("administration:SiteCreation:_view");
	const siteAcct = user.roles.includes("administration:SiteCreation:_editAcct") && !user.roles.includes("_TH:GY:ACCT");
	const siteAdmin = user.roles.includes("administration:SiteCreation:_admin");

	const loaderMounted = (boo) => {
		if (boo) {
			setShowLoader({position: "fixed"});
			setNeedLoader(true);
		} else {
			setShowLoader({position: "relative"});
			setNeedLoader(false);
		}
	};

	function compareStatus (a, b) {
		if (a.sorting < b.sorting) {
			return -1;
		}
		if (a.sorting > b.sorting) {
			return 1;
		}
		return 0;
	}

	/**
	 * Get data from back-end for Construction Site Table
	 */
	const fetchData = useCallback(async () => {
		const uri = siteAdmin ? `/site_creation/savedsites/acct/null` : `/site_creation/savedsites/acct/${loggedCountry}`;

		const acctInfo = await GYAxios.get(uri).catch((error) => {console.log(error);});

		if (acctInfo?.data) {
			await acctInfo.data.forEach((v) => {
				v.dateCreated = moment(v.dateCreated).format("YYYY-MM-DD");

				if (v.isSent === 0 && v.isRejected === 2) {
					v.site_status = v.lastUpdatedBy === null ? "Information Required" : "Saved";
					v.sorting = "a";
				} else if (v.isSent === 1 && v.isRejected === 2) {
					v.site_status = "Waiting for Department Manager's Approval";
					v.sorting = "b";
				} else if (v.isSent === 1 && v.isRejected === 1) {
					v.site_status = `Rejected because ${v.rejectedRemark}`;
					v.sorting = "c";
				} else if (v.isSent === 1 && v.isRejected === 0 && v.isExecuted === 0) {
					v.site_status = "Approved. Waiting for IT's creation";
					v.sorting = "d";
				} else if (v.isSent === 1 && v.isRejected === 0 && v.isExecuted === 1) {
					v.site_status = "Executed";
					v.sorting = "e";
				}
			});

			const tempActions = [];
			await siteView && tempActions.push(() => {
				return {
					icon: "visibility",
					tooltip: <p style={{fontSize: "2em"}}>View</p>,
					onClick: (e, rowData) => passInfoToForm(e, {...rowData}),
				};
			});


			await siteAcct && tempActions.push(
				(rowData) => {
					return rowData.site_code !== null &&
						rowData.location !== null &&
						rowData.currency !== null &&
						rowData.EA_id !== null &&
						rowData.tariff_id !== null &&
						rowData.solar_entity !== null &&
						rowData.discount_on !== null &&
						rowData.discount_off !== null &&
						rowData.FT_discount !== null &&
						rowData.credit_term !== null &&
						rowData.tax_id !== null &&
						rowData.bank_id !== null &&
						rowData.billing_address !== null &&
						rowData.billing_name !== null &&
						rowData.price_on !== null &&
						rowData.price_off !== null &&
						rowData.price_id !== null &&
						rowData.isSent === 0 &&
						rowData.isRejected === 2
						? {
							icon: "send",
							tooltip: <p style={{fontSize: "2em"}}>Send to Department Manager for Approval</p>,
							onClick: (e, rowData) => {sendEmail(rowData);},
						} : null;
				},
				(rowData) => {
					return (rowData.isRejected === 2 && rowData.isSent === 0)
						? {
							icon: "clear",
							tooltip: <p style={{fontSize: "2em"}}>Clear data</p>,
							onClick: (e, rowData) => clearData(rowData),
						} : null;
				}
			);

			return {
				data: acctInfo.data.sort((a, b) => compareStatus(a, b) || moment(b.dateCreated).diff(moment(a.dateCreated))),
				actions: tempActions,
			};
		} else {
			const msg = "Error fetching data from server.";
			Noti.create({type: "e", msg});
			return {};
		}
	}, [loggedCountry]);

	const sendEmail = async (rowData) => {
		const question = "Are you sure you want to send this site to Department Manager for approval?";
		const confirm = window.confirm(question);
		if (!confirm) return;

		loaderMounted(true);
		try {
			const bank_details = await GYAxios.get(`/select/banks/${loggedCountry}`).then((res) => {
				if (res.data.length !== 0) return res.data.filter((v) => v.bank_id === rowData.bank_id)[0];
				return {};
			}).catch((error) => {
				Noti.create({type: 'e', msg: 'Something is wrong.'});
				setNeedLoader(false);
				return {};
			});
			await GYAxios.post("/site_creation/sendEmailToDepManagerForApproval", {...rowData, role: "administration:SiteCreation:_approvalAcct", email: user.email, isSent: 1, bank_details}).then((res) => {
				Noti.create({type: 's', msg: res.data.message});
				const timer = setTimeout(() => {
					loaderMounted(false);
					reload();
				}, 1000);
				return () => clearTimeout(timer);
			}).catch((error) => {
				Noti.create({type: 'e', msg: 'Something is wrong.'});
				setNeedLoader(false);
				return;
			});
		} catch (err) {
			Noti.create({type: 'e', msg: 'Something is wrong.'});
			setNeedLoader(false);
		}
	};

	const clearData = async (rowData) => {
		const question = "Are you sure you want to clear every saved data of this site?";
		const confirm = window.confirm(question);
		if (!confirm) return;

		loaderMounted(true);
		try {
			await GYAxios.patch(`/site_creation/clear_acct_info/${rowData.acct_id}`).then((res) => {
				Noti.create({type: 's', msg: res.data.message});
				const timer = setTimeout(() => {
					loaderMounted(false);
					reload();
				}, 1000);
				return () => clearTimeout(timer);
			}).catch((error) => {
				Noti.create({type: 'e', msg: 'Something is wrong.'});
				setNeedLoader(false);
				return;
			});
		} catch (err) {
			Noti.create({type: 'e', msg: 'Something is wrong.'});
			setNeedLoader(false);
		}
	};

	/**
	 * Passing data of selected site to SiteForm
	 * through SiteCreation
	 * @param {Object} data selected site
	 */
	function passInfoToForm (e, data) {
		const payload = { siteInfo: data }
		openLink(e, '/solar/administration/site-creation/manage-site/invoicing-info-form', payload);
	}

	function reload () {
		setReady(false);
		const getData = async () => {
			const {data, actions} = await fetchData();
			setData(data);
			setActs(actions);
		};
		getData().then(() => setReady(true));
	}

	useEffect(() => {
		if (!ready) {
			const getData = async () => {
				const {data, actions} = await fetchData();
				setData(data);
				setActs(actions);
			};
			getData().then(() => setReady(true));
		}
	}, []);

	return (
		(siteView) && <>
			<MaterialTable
				title={
					<div>
						<h5>
							Invoicing Info Sites Table{" "}
							<span
								className="	glyphicon glyphicon-refresh"
								onClick={() => reload()}
							></span>
						</h5>
					</div>
				}
				columns={otherscols}
				isLoading={!ready}
				data={data}
				actions={acts}
				options={optionsTable(data)}
				style={{marginBottom: "10px"}}
				detailPanel={(rowData) => {
					return (
						<ul style={styleDetails}>
							{rowData.rejectedRemark !== null ? <li>REJECTED REMARK: {rowData.rejectedRemark}</li> : ""}
							<li>Last updated by: {rowData.lastUpdatedBy || "-"}</li>
							<li>
								Last updated at:{" "}
								{rowData.lastUpdatedAt ? moment(rowData.lastUpdatedAt).format("DD/MM/YYYY") : "-"}
							</li>
						</ul>
					);
				}}
			/>
			{needLoader && <div className="overlay translucent" style={showLoader}>
				<Loader
					translucent={true}
					customStyle={{
						width: "150px", height: "150px", position: "fixed", left: "50%", top: "35%",
					}}
				/>
			</div>}
		</>
	);
}

export default React.memo(AccountingTable);
